/**
 * Capacitor-Bruecke — alles, was NUR in der nativen Huelle (iOS, macOS) gilt.
 *
 * Regel fuer dieses Modul: im Browser passiert hier NICHTS. Jede Funktion
 * prueft zuerst `isNative()` und kehrt sonst sofort zurueck, damit der Web-Build
 * unveraendert bleibt und dieselbe Datei ohne Fallunterscheidung importiert
 * werden kann.
 *
 * Die Plugin-Importe stehen bewusst als dynamische `import()` in den Handlern:
 * so landet der native Code nicht im Start-Chunk des Web-Builds.
 */
import { Capacitor } from '@capacitor/core'

/** Laeuft die App in einer Capacitor-Huelle (iOS oder Electron/macOS)? */
export function isNative(): boolean {
  return Capacitor.isNativePlatform()
}

/** iOS-Geraet (iPhone/iPad) — NICHT der Mac-Build. */
export function isIOS(): boolean {
  return Capacitor.getPlatform() === 'ios'
}

/** macOS-Desktop-Build ueber @capacitor-community/electron. */
export function isElectron(): boolean {
  return Capacitor.getPlatform() === 'electron'
}

/**
 * Externe Links ins System-Browserfenster umleiten.
 *
 * In der WKWebView tut ein `<a target="_blank">` schlicht gar nichts: es gibt
 * kein zweites Fenster, und `window.open` liefert null. Betroffen sind u. a.
 * die Belegs- und Mandats-Links in [ClientDetail.vue](../../views/clients/ClientDetail.vue)
 * und [Zahlungsabgleich.vue](../../views/finance/Zahlungsabgleich.vue).
 *
 * Der Listener haengt in der Capture-Phase am Dokument, damit er auch bei
 * Links innerhalb von Modals und Tabellen greift, ohne dass dort etwas
 * angepasst werden muss.
 */
function interceptExternalLinks(): void {
  document.addEventListener(
    'click',
    (event) => {
      const anchor = (event.target as HTMLElement | null)?.closest?.('a')
      if (!anchor) return

      const href = anchor.getAttribute('href') || ''
      // Nur echte externe Ziele. Interne Router-Links, Anker (#) sowie
      // blob:/data:-URLs (PDF-Vorschauen) muessen in der WebView bleiben.
      if (!/^https?:\/\//i.test(href)) return

      event.preventDefault()

      if (isElectron()) {
        // Auf dem Mac uebernimmt die Huelle: `window.open` laeuft in den
        // setWindowOpenHandler aus electron/src/setup.ts, der externe Ziele an
        // das System-Browserfenster weiterreicht. Das Browser-Plugin hat keine
        // Electron-Implementierung und wuerde hier werfen.
        window.open(href, '_blank')
        return
      }

      import('@capacitor/browser').then(({ Browser }) => {
        Browser.open({ url: href })
      })
    },
    true
  )
}

/**
 * Native Huelle initialisieren. Aufruf aus [main.ts](../../main.ts), noch vor
 * `app.mount()` — die Listener muessen stehen, bevor die erste View rendert.
 */
export async function initNative(): Promise<void> {
  if (!isNative()) return

  // Die Safe-Area-Abstaende in public/css/custom.css haengen an
  // `@media (display-mode: standalone)` bzw. `html.pwa-standalone`. In der
  // Capacitor-WebView greift die Media-Query NICHT (das ist kein installiertes
  // Web-App-Fenster) und `navigator.standalone` ist undefined — ohne diese
  // Klasse liefe der Header unter die Notch. Nur auf iOS: der Mac hat weder
  // Notch noch Home-Indicator, dort wuerde der Abstand nur Platz kosten.
  if (isIOS()) {
    document.documentElement.classList.add('pwa-standalone')
  }

  interceptExternalLinks()

  // iOS friert im Hintergrund die WebView samt Workern ein und kappt den
  // Socket. `visibilitychange` (App.vue) feuert in der WKWebView nicht
  // zuverlaessig, wenn die App als Ganzes in den Hintergrund geht — der
  // App-State des Systems ist die verlaessliche Quelle. Die Nachricht ist
  // dieselbe, die App.vue bei Sichtbarkeit schickt; der Worker ist gegen
  // Mehrfach-Wecken ohnehin robust.
  const { App: CapApp } = await import('@capacitor/app')
  CapApp.addListener('appStateChange', ({ isActive }) => {
    if (!isActive) return
    import('@/core/worker/workerInstances').then(({ serverWorker }) => {
      serverWorker.postMessage({ cmd: 'visibility', task: 'visible' })
    })
  })

  if (isIOS()) {
    const { StatusBar, Style } = await import('@capacitor/status-bar')
    // Ueberlagernd + helle Schrift: die Statusleiste liegt auf dem
    // #18202f-Streifen, den `body::before` aus custom.css zeichnet.
    await StatusBar.setOverlaysWebView({ overlay: true }).catch(() => {})
    await StatusBar.setStyle({ style: Style.Dark }).catch(() => {})
  }
}

/**
 * Nativen Splash ausblenden. Aufruf erst, wenn die erste View wirklich steht
 * (App.vue, direkt nach `removeBodyClassName('page-loading')`) — der Splash ist
 * in capacitor.config.ts auf `launchAutoHide: false` gestellt, damit zwischen
 * ihm und der fertigen Oberflaeche kein weisser Frame steht.
 */
export async function hideNativeSplash(): Promise<void> {
  if (!isNative()) return
  const { SplashScreen } = await import('@capacitor/splash-screen')
  await SplashScreen.hide().catch(() => {})
}
